import React from "react";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";

const LayoutPage: React.FC = () => {
  const [collapsibleOpen, setCollapsibleOpen] = React.useState(false);
  const [tabValue, setTabValue] = React.useState("tab1");

  return (
    <div className="w-full px-12 py-12">
      <header className="mb-12">
        <p className="mb-1 text-sm font-medium text-brand">Design System</p>
        <h1 className="mb-3 font-sans text-2xl font-bold tracking-tight text-stone-900">Layout</h1>
        <p className="text-sm leading-relaxed text-stone-500">
          Components for organizing and structuring content.
        </p>
      </header>

      <div className="space-y-16">
        {/* Accordion */}
        <section id="accordion" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Accordion</h2>
            <p className="mt-1 text-sm text-stone-500">
              Expandable content panels for organizing information.
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger>Is it accessible?</AccordionTrigger>
              <AccordionContent>Yes. It adheres to the WAI-ARIA design pattern.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>Is it styled?</AccordionTrigger>
              <AccordionContent>
                Yes. It comes with default styles that matches the other components&apos; aesthetic.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>Is it animated?</AccordionTrigger>
              <AccordionContent>
                Yes. It&apos;s animated by default, but you can disable it if you prefer.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>

        {/* Collapsible */}
        <section id="collapsible" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Collapsible</h2>
            <p className="mt-1 text-sm text-stone-500">
              Expandable container for toggling content visibility.
            </p>
          </div>
          <Collapsible open={collapsibleOpen} onOpenChange={setCollapsibleOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                Click to expand
                {collapsibleOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="rounded-md border px-4 py-3 text-sm">
                This is the collapsible content. It can contain any elements you want.
              </div>
            </CollapsibleContent>
          </Collapsible>
        </section>

        {/* Separator */}
        <section id="separator" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Separator</h2>
            <p className="mt-1 text-sm text-stone-500">Visual divider between content sections.</p>
          </div>
          <div className="space-y-4">
            <div>Content above separator</div>
            <Separator />
            <div>Content below separator</div>
          </div>
        </section>

        {/* Tabs */}
        <section id="tabs" className="scroll-mt-8 space-y-4">
          <div className="mb-4 border-b border-stone-200 pb-3">
            <h2 className="font-sans text-lg font-semibold text-stone-900">Tabs</h2>
            <p className="mt-1 text-sm text-stone-500">Organize content into switchable panels.</p>
          </div>
          <Tabs value={tabValue} onValueChange={setTabValue} className="w-full">
            <TabsList>
              <TabsTrigger value="tab1">Tab 1</TabsTrigger>
              <TabsTrigger value="tab2">Tab 2</TabsTrigger>
              <TabsTrigger value="tab3">Tab 3</TabsTrigger>
            </TabsList>
            <TabsContent value="tab1" className="rounded-md border p-4">
              Content for Tab 1
            </TabsContent>
            <TabsContent value="tab2" className="rounded-md border p-4">
              Content for Tab 2
            </TabsContent>
            <TabsContent value="tab3" className="rounded-md border p-4">
              Content for Tab 3
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </div>
  );
};

export default LayoutPage;
